import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { CloudOff, Refresh } from '@mui/icons-material';

import Login from './Login';
import SetupWizard from './Setup/SetupWizard';
import { setup_status } from './BaseURL';
import { BRAND, PRODUCT, FONT_STACK, brandButtonSx, GRADIENT_GREEN } from '../brand';

// ============================================================================
//  SystemStart — the boot gate
// ============================================================================
//
//  Everything unauthenticated enters through here. On a cold start it asks the
//  server one question: has this installation been set up yet?
//
//    setupRequired  → render the Setup wizard, and ONLY the wizard. A system
//                     with no facility and no users has no meaningful login
//                     screen, so we never show one.
//    otherwise      → render Login.
//
//  If the status call fails we deliberately do NOT guess. Falling through to
//  the wizard on a network blip would invite a second setup attempt on a live
//  system; falling through to login would strand a genuinely fresh install.
//  So we show a retry screen and let the operator decide when to try again.

const STATE = { LOADING: 'loading', SETUP: 'setup', LOGIN: 'login', ERROR: 'error' };

function SystemStart() {
  const [phase, setPhase] = useState(STATE.LOADING);
  const [activationRequired, setActivationRequired] = useState(false);
  const [error, setError] = useState('');

  const checkStatus = useCallback(async () => {
    setPhase(STATE.LOADING);
    setError('');

    try {
      const { data } = await axios.get(setup_status, { timeout: 15000 });

      if (data?.setupRequired) {
        setActivationRequired(!!data.activationRequired);
        setPhase(STATE.SETUP);
      } else {
        setPhase(STATE.LOGIN);
      }
    } catch (err) {
      // A 404 means this backend predates setup_routes.js — an older server
      // that is, by definition, already past setup. Send those to login
      // rather than blocking a working deployment on a missing endpoint.
      if (err.response?.status === 404) {
        setPhase(STATE.LOGIN);
        return;
      }
      setError(
        err.response?.data?.message ||
        'Could not reach the server to check the system status.'
      );
      setPhase(STATE.ERROR);
    }
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  // ── Splash while the gate is being read ─────────────────────────────────
  if (phase === STATE.LOADING) {
    return (
      <Box
        sx={{
          minHeight: '100vh', display: 'grid', placeItems: 'center',
          background: BRAND.canvas, fontFamily: FONT_STACK,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 60, height: 60, borderRadius: '15px', mx: 'auto', mb: 3,
              background: GRADIENT_GREEN, display: 'grid', placeItems: 'center',
              boxShadow: `0 10px 30px ${BRAND.green}40`,
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 22, color: '#fff', letterSpacing: '-.5px' }}>
              GA
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 26, fontWeight: 900, color: BRAND.navy, letterSpacing: '-1px', lineHeight: 1 }}>
            {PRODUCT.name}
          </Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: BRAND.green, textTransform: 'uppercase', letterSpacing: '.8px', mt: .75, mb: 3 }}>
            {PRODUCT.vendor}
          </Typography>

          <CircularProgress size={22} sx={{ color: BRAND.navy }} />
          <Typography sx={{ mt: 2, fontSize: 13, color: BRAND.inkMuted }}>
            Starting up…
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Could not determine the state — retry rather than guess ─────────────
  if (phase === STATE.ERROR) {
    return (
      <Box
        sx={{
          minHeight: '100vh', display: 'grid', placeItems: 'center',
          background: BRAND.canvas, fontFamily: FONT_STACK, p: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: 440, width: '100%', textAlign: 'center', background: '#fff',
            borderRadius: '18px', p: { xs: 3.5, sm: 5 },
            border: `1px solid ${BRAND.line}`, boxShadow: '0 16px 48px rgba(15,31,61,.08)',
          }}
        >
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2.5, display: 'grid', placeItems: 'center', background: '#FDECEC' }}>
            <CloudOff sx={{ fontSize: 32, color: BRAND.danger }} />
          </Box>

          <Typography sx={{ fontSize: 19, fontWeight: 800, color: BRAND.ink, mb: 1.25 }}>
            Cannot reach the server
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: BRAND.inkMuted, lineHeight: 1.75, mb: 3.5 }}>
            {error} Check that the server is running and that this device is on the
            network, then try again.
          </Typography>

          <Button
            fullWidth variant="contained" startIcon={<Refresh />}
            onClick={checkStatus} sx={{ ...brandButtonSx, py: 1.25 }}
          >
            Try again
          </Button>
        </Box>
      </Box>
    );
  }

  if (phase === STATE.SETUP) {
    return (
      <SetupWizard
        activationRequired={activationRequired}
        // After a successful setup, re-read the gate instead of flipping a
        // local flag — that way the login screen loads the facility name and
        // logo the wizard just wrote.
        onComplete={() => setPhase(STATE.LOGIN)}
      />
    );
  }

  return <Login />;
}

export default SystemStart;
